import {hydateQestions} from 'utils/user_bio';

const CAREOS_STATUS_BY_LOOM_STATUS = {
  PROFILE_CREATED: 'PROFILE_PENDING',
  PROFILE_COMPLETE: 'ENGAGED',
  INTERVIEW_COMPLETE: 'INTERVIEW_REVIEW',
  IN_REVIEW: 'PROFILE_REVIEW',
  REJECTED: 'INACTIVE',
  ACTIVE: 'ACTIVE',
  PAUSED: 'INACTIVE',
  INACTIVE: 'INACTIVE',
};

const CREDENTIALS_BY_CAREOS_FILTER = {
  psw: ['PSW', 'PAB', 'PSW_STUDENT'],
  dsw: ['DSW'],
  nursing: ['RN', 'RPN', 'RNA', 'CNA', 'NURSING_STUDENT'],
  firstAid: ['FIRST_AID'],
  cpr: ['CPR'],
};

const CAREOS_SKILL_CATEGORY_BY_LOOM_TYPE = {
  MEMORY_CARE: 'memoryCare',
  MOBILITY_SUPPORT: 'mobilitySupport',
  PERSONAL_CARE: 'personalCare',
  MEDICATION_REMINDERS: 'medicationReminders',
  MEAL_PREPARATION: 'mealPreparation',
  LIGHT_HOUSEKEEPING: 'lightHousekeeping',
};

const CAREOS_SKILL_LEVEL_BY_LOOM_PROFICIENCY = {
  memoryCare: {
    BASIC: 'earlyStage',
    INTERMEDIATE: 'midStage',
    ADVANCED: 'lateStage',
  },
  mobilitySupport: {
    BASIC: 'lowSupport',
    INTERMEDIATE: 'moderateSupport',
    ADVANCED: 'highSupport',
  },
  personalCare: {
    BASIC: 'someAssistance',
    INTERMEDIATE: 'partialCare',
    ADVANCED: 'fullCare',
  },
  medicationReminders: {
    BASIC: 'remindersOnly',
    INTERMEDIATE: 'remindersOnly',
    ADVANCED: 'remindersOnly',
  },
  mealPreparation: {
    BASIC: 'simpleMeals',
    INTERMEDIATE: 'someMealSupport',
    ADVANCED: 'fullMealSupport',
  },
  lightHousekeeping: {
    BASIC: 'occasionalTidying',
    INTERMEDIATE: 'regularCleaning',
    ADVANCED: 'ongoingHouseholdUpkeep',
  },
};

// TODO: temporary file handling. CareOS file records carry a name and a MIME type
// alongside the URL, but Loom stores only the URL (caregiver.profile_image_url and
// caregiver_screening.document_url are both a bare text column), and the ingest drops
// the other two fields. Both are derived from the URL here so the existing file
// components keep working. Unknown extensions fall back to a generic binary type,
// which renders as a downloadable file rather than an inline image. Delete this and
// read the stored values once Loom keeps the file name and MIME type.
const MIME_TYPE_BY_EXTENSION = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
  pdf: 'application/pdf',
};

const DEFAULT_MIME_TYPE = 'application/octet-stream';

const unique = (values) => [...new Set(values.filter(Boolean))];

const normalizedName = (value) => {
  if (typeof value !== 'string') return value;

  return value
      .trim()
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      .replace(/[\s-]+/g, '_')
      .replace(/_+/g, '_')
      .toUpperCase();
};

const pascalCaseName = (value) => {
  return `${value || ''}`
      .toLowerCase()
      .split('_')
      .filter(Boolean)
      .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
      .join('');
};

const camelCaseName = (value) => {
  const pascalCase = pascalCaseName(value);
  return `${pascalCase.charAt(0).toLowerCase()}${pascalCase.slice(1)}`;
};

const isUnavailableFileUrl = (url) => {
  const fileStorageDomain = process.env.REACT_APP_FILE_STORAGE_DOMAIN;
  if (!fileStorageDomain) return false;

  try {
    const fileUrl = new URL(url);
    const configuredFileUrl = new URL(fileStorageDomain);
    const isCareQuiltCloudFrontUrl = fileUrl.hostname.endsWith('.cloudfront.net') &&
      configuredFileUrl.hostname.endsWith('.cloudfront.net');

    return isCareQuiltCloudFrontUrl && fileUrl.origin !== configuredFileUrl.origin;
  } catch {
    return false;
  }
};

// TODO: temporary, see MIME_TYPE_BY_EXTENSION.
const buildFile = (url) => {
  if (!url) return undefined;

  const name = decodeURIComponent(url.split('?')[0].split('/').pop() || 'file');
  const extension = name.includes('.') ? name.split('.').pop().toLowerCase() : '';

  return {
    name,
    type: MIME_TYPE_BY_EXTENSION[extension] || DEFAULT_MIME_TYPE,
    src: url,
    unavailable: isUnavailableFileUrl(url),
  };
};

const buildFiles = (url) => {
  const file = buildFile(url);
  return file ? [file] : [];
};

const primaryAddress = (caregiver) => {
  const addresses = caregiver?.person?.addresses || [];
  const home = addresses.find((entry) => entry.type === 'HOME');
  return home?.address || addresses[0]?.address || {};
};

const caregiverName = (caregiver) => {
  return [caregiver?.person?.firstName, caregiver?.person?.lastName]
      .filter(Boolean)
      .join(' ');
};

const buildContacts = (caregiver) => {
  const contacts = {};

  if (caregiver?.person?.email) {
    contacts.Email = {
      type: 'Email',
      detail: caregiver.person.email,
    };
  }
  if (caregiver?.person?.phoneNumber) {
    contacts['Phone number'] = {
      type: 'Phone number',
      detail: caregiver.person.phoneNumber,
    };
  }

  return contacts;
};

const buildClients = (caregiver) => {
  return (caregiver?.clientAssignments || []).reduce((result, assignment) => {
    const client = assignment?.client;
    const clientRn = client?.legacyClientRn;

    if (!clientRn) return result;

    result[clientRn] = {
      clientRn,
      clientName: [client?.person?.firstName, client?.person?.lastName]
          .filter(Boolean)
          .join(' '),
      status: assignment.status === 'PENDING' ? 'REVIEW' : assignment.status,
    };
    return result;
  }, {});
};

const buildCertificates = (caregiver) => {
  return (caregiver?.credentials || [])
      .filter((entry) => entry?.credential?.name)
      .map((entry) => ({
        id: entry.id,
        type: entry.credential.name,
        issuingOrganization: entry.issuingOrganization,
        dateObtained: entry.issuedAt,
        city: entry.city,
        attachments: [],
      }));
};

const buildLanguages = (caregiver) => {
  return (caregiver?.languages || []).map((entry) => ({
    name: pascalCaseName(entry?.language?.name),
    fluency: pascalCaseName(entry.proficiency),
    interview: entry.interview ? {
      callReferenceId: entry.interview.callReferenceId,
    } : undefined,
  }));
};

const buildWorkHistory = (caregiver) => {
  return (caregiver?.workHistory || []).map((entry) => ({
    id: entry.id,
    employerName: entry.employer,
    startDate: entry.startDate,
    endDate: entry.endDate,
  }));
};

const buildReferences = (caregiver) => {
  return (caregiver?.references || []).map((entry) => ({
    id: entry.id,
    name: entry.name,
    email: entry.email,
    phoneNumber: entry.phoneNumber,
    relationship: entry.relationship,
    referenceRn: entry.legacyReferenceRn,
  }));
};

const buildEducation = (caregiver) => ({
  entries: (caregiver?.education || []).map((entry) => ({
    id: entry.id,
    school: entry.institution,
    fieldOfStudy: entry.program,
    degree: entry.notes || entry.educationLevel,
    dateObtained: entry.completedAt,
    documents: [],
  })),
});

const buildPreferences = (caregiver) => {
  const lengthOfShifts = (caregiver.shiftLengths || []).reduce((result, entry) => {
    result[entry.lengthOfShift] = true;
    return result;
  }, {});
  const values = {
    mainTravelMethod: caregiver.mainTravelMethod,
    maxTravelTime: caregiver.maxTravelTime,
    catAndDogComfort: caregiver.catDogComfort,
    lengthOfShifts,
  };

  const hasPreferences = caregiver.mainTravelMethod || caregiver.maxTravelTime ||
    caregiver.catDogComfort || Object.keys(lengthOfShifts).length;
  return hasPreferences ? values : undefined;
};

const buildAvailability = (caregiver) => {
  const weeklyAvailability = (caregiver.availability || []).reduce((result, entry) => {
    const day = pascalCaseName(entry.dayOfWeek);
    if (!result[day]) {
      result[day] = {customRanges: [], presetRanges: {}};
    }
    result[day].customRanges.push({
      start: entry.startTime?.slice(0, 5),
      end: entry.endTime?.slice(0, 5),
    });
    return result;
  }, {});

  const values = {
    scheduleTypePref: caregiver.scheduleType,
    workHoursCapacity: caregiver.workHoursCapacity,
    earliestStart: caregiver.earliestStart,
    weeklyAvailability,
  };

  const hasAvailability = caregiver.scheduleType || caregiver.workHoursCapacity ||
    caregiver.earliestStart || Object.keys(weeklyAvailability).length;
  return hasAvailability ? values : undefined;
};

const buildSkills = (caregiver) => {
  const categories = {};

  for (const entry of caregiver?.skills || []) {
    const category = CAREOS_SKILL_CATEGORY_BY_LOOM_TYPE[entry?.skill?.type];
    if (!category) continue;

    if (!categories[category]) {
      categories[category] = {
        skill: {selected: true},
        level: {value: CAREOS_SKILL_LEVEL_BY_LOOM_PROFICIENCY[category][entry.proficiency]},
        tasks: {value: []},
      };
    }
    categories[category].tasks.value.push(camelCaseName(entry.skill.name));
  }

  return {
    categories,
    selected: Object.keys(categories),
  };
};

const buildPersonality = (caregiver) => {
  if (!caregiver.personality) return undefined;

  const answers = {
    busyMomentStyle: caregiver.personality.busyMomentStyle,
    carePlanningApproach: caregiver.personality.carePlanningApproach,
    communicationStyle: caregiver.personality.communicationStyle,
    conflictApproach: caregiver.personality.conflictApproach,
    energyDuringLongShift: caregiver.personality.energyDuringLongShift,
    feedbackResponse: caregiver.personality.feedbackResponse,
    newClientFirstShift: caregiver.personality.newClientFirstShift,
    preferredWorkRhythm: caregiver.personality.preferredWorkRhythm,
    unexpectedShiftFirstAction: caregiver.personality.unexpectedShiftFirstAction,
    whatFamiliesNoticeMost: caregiver.personality.whatFamiliesNoticeMost,
  };
  const questions = Object.entries(answers).reduce((result, [key, value]) => {
    if (value) result[key] = value;
    return result;
  }, {});

  return Object.keys(questions).length ? {questions} : undefined;
};

const buildScreening = (caregiver, category, presenceKey) => {
  const entry = (caregiver?.screenings || [])
      .find((screening) => screening?.screening?.category === category);

  if (!entry) return undefined;

  return {
    [presenceKey]: true,
    hasBeenVerified: entry.status === 'VERIFIED',
    documents: buildFiles(entry.documentUrl),
  };
};

const buildQuestions = (caregiver) => {
  const address = primaryAddress(caregiver);
  const skills = buildSkills(caregiver);
  const personality = buildPersonality(caregiver);
  const values = {
    firstName: caregiver?.person?.firstName,
    lastName: caregiver?.person?.lastName,
    email: caregiver?.person?.email,
    phoneNumber: caregiver?.person?.phoneNumber,
    streetAddress: address.line1 || address.legacyAddress,
    city: address.city,
    province: address.region,
    postalCode: address.postalCode,
    shortBio: caregiver.shortDescription,
    personalBio: caregiver.description,
    caregiverMotivation: caregiver.motivation,
    profilePhoto: buildFiles(caregiver.profileImageUrl),
    languages: buildLanguages(caregiver),
    languagesCompleted: (caregiver.languages || []).length > 0,
    preferences: buildPreferences(caregiver),
    availability: buildAvailability(caregiver),
    certificates: buildCertificates(caregiver),
    vulnerableSectorCheck: buildScreening(caregiver, 'VULNERABLE_SECTOR', 'hasCheck'),
    canadianId: buildScreening(caregiver, 'CANADIAN_ID', 'hasId'),
    education: caregiver.education?.length ? buildEducation(caregiver) : undefined,
    workHistory: buildWorkHistory(caregiver),
    careTasks: skills.selected.length ? {selected: skills.selected} : undefined,
    ...skills.categories,
    skillsExperienceCompleted: skills.selected.length > 0,
    personality,
    personalityCompleted: Boolean(personality),
    references: buildReferences(caregiver),
  };

  return hydateQestions(values);
};

const buildCaregiver = (caregiver) => ({
  caregiverRn: caregiver.legacyCaregiverRn,
  caregiverName: caregiverName(caregiver),
  status: CAREOS_STATUS_BY_LOOM_STATUS[caregiver.status] || 'PROFILE_PENDING',
  description: {
    blob: caregiver.description || '',
    editorType: 'MARKDOWN',
  },
  profileImg: caregiver.profileImageUrl,
  contacts: buildContacts(caregiver),
  clients: buildClients(caregiver),
  bio: {
    caregiverRn: caregiver.legacyCaregiverRn,
    questions: buildQuestions(caregiver),
  },
  createdAt: caregiver.createdAt,
  updatedAt: caregiver.updatedAt,
});

const buildInterviews = (caregiver) => {
  return (caregiver?.interviews || []).map((entry) => ({
    ...entry,
    caregiverRn: caregiver.legacyCaregiverRn,
    interviewRn: entry.id,
    conversationId: entry.callReferenceId,
    convUrl: entry.conversationUrl,
    interviewType: entry.type === 'LANGUAGE' && entry.notes ?
      `LANGUAGE_${entry.notes}` : entry.type,
    // TODO: temporary. The migration drops the data collection and evaluation CareOS
    // interviews carry, and caregiver_interview has no column for either, so the panels
    // driven by them render empty. Read the stored values once Loom keeps them.
    dataCollection: entry.dataCollection || {},
    evaluation: entry.evaluation || null,
  }));
};

export const toLoomCaregiverListQuery = (filters, sort, cursor, options = {}) => {
  const query = {
    limit: options.limit || 25,
    cursor,
    sortDirection: sort?.sortDirection?.toLowerCase() || 'desc',
  };
  const statuses = [];
  const languages = [];
  const credentials = [];

  for (const filter of filters || []) {
    if (filter.key === 'caregiverName') {
      query.name = typeof filter.value === 'string' ?
        filter.value.replace(/\*+$/, '').trim() :
        filter.value;
    } else if (filter.key === 'status') {
      const values = Array.isArray(filter.value) ? filter.value : [filter.value];
      statuses.push(...values);
    } else if (filter.key === 'languages') {
      const values = Array.isArray(filter.value) ? filter.value : `${filter.value || ''}`.split(' ');
      languages.push(...values.map(normalizedName));
    } else if (CREDENTIALS_BY_CAREOS_FILTER[filter.key] && filter.value) {
      credentials.push(...CREDENTIALS_BY_CAREOS_FILTER[filter.key]);
    }
  }

  if (statuses.length) query.status = unique(statuses).join(',');
  if (languages.length) query.language = unique(languages).join(',');
  if (credentials.length) query.credential = unique(credentials).join(',');

  return query;
};

export const adaptLoomCaregiverList = (response) => ({
  list: (response?.caregivers || [])
      .filter((caregiver) => caregiver?.legacyCaregiverRn)
      .map((caregiver) => ({
        ...buildCaregiver(caregiver),
        status: caregiver.status,
      })),
  lastEvaluatedKey: response?.nextCursor || null,
});

export const adaptLoomCaregiverDetail = (caregiver) => {
  const adapted = buildCaregiver(caregiver);

  return {
    caregiverRn: caregiver.legacyCaregiverRn,
    caregiverDetail: adapted,
    caregiverBio: adapted.bio,
    shortIdRecord: {},
    caregiverReviews: {list: []},
    caregiverInterviews: {list: buildInterviews(caregiver)},
  };
};
